import { useState, useCallback } from 'react';
import { ZodError, ZodSchema } from 'zod';

export interface ValidationError {
  field: string;
  message: string;
}

export interface UseFormValidationReturn<T> {
  /** Valider les données avec le schema */
  validate: (data: unknown) => { success: true; data: T } | { success: false; errors: ValidationError[] };
  /** Valider un champ individuel */
  validateField: (field: keyof T, value: unknown) => string | null;
  /** Erreurs de validation actuelles */
  errors: Record<string, string>;
  /** Définir les erreurs manuellement */
  setErrors: (errors: Record<string, string>) => void;
  /** Effacer toutes les erreurs */
  clearErrors: () => void;
  /** Effacer l'erreur d'un champ spécifique */
  clearFieldError: (field: string) => void;
  /** Vérifier si le formulaire a des erreurs */
  hasErrors: boolean;
  /** Obtenir l'erreur d'un champ */
  getFieldError: (field: string) => string | undefined;
}

/**
 * Hook pour la validation de formulaires avec Zod
 *
 * @example
 * ```tsx
 * const { validate, errors, getFieldError } = useFormValidation(loginSchema);
 *
 * const handleSubmit = () => {
 *   const result = validate({ email, password });
 *   if (result.success) {
 *     // Données validées
 *     await login(result.data);
 *   }
 * };
 *
 * return (
 *   <Input
 *     value={email}
 *     error={getFieldError('email')}
 *   />
 * );
 * ```
 */
export function useFormValidation<T>(schema: ZodSchema<T>): UseFormValidationReturn<T> {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = useCallback(
    (data: unknown): { success: true; data: T } | { success: false; errors: ValidationError[] } => {
      try {
        const validData = schema.parse(data);
        setErrors({});
        return { success: true, data: validData };
      } catch (error) {
        if (error instanceof ZodError) {
          const validationErrors: ValidationError[] = error.errors.map((err) => ({
            field: err.path.join('.'),
            message: err.message,
          }));

          const errorMap: Record<string, string> = {};
          validationErrors.forEach((err) => {
            errorMap[err.field] = err.message;
          });
          setErrors(errorMap);

          return { success: false, errors: validationErrors };
        }
        throw error;
      }
    },
    [schema]
  );

  const validateField = useCallback(
    (field: keyof T, value: unknown): string | null => {
      try {
        // Créer un objet partiel avec juste le champ à valider
        const partialData = { [field]: value };

        // Utiliser safeParse pour ne pas lever d'exception
        const result = schema.safeParse(partialData);

        if (!result.success) {
          const fieldError = result.error.errors.find(
            (err) => err.path[0] === field
          );
          if (fieldError) {
            setErrors((prev) => ({ ...prev, [field as string]: fieldError.message }));
            return fieldError.message;
          }
        }

        // Effacer l'erreur du champ si valide
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field as string];
          return newErrors;
        });
        return null;
      } catch {
        return null;
      }
    },
    [schema]
  );

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  const clearFieldError = useCallback((field: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  const getFieldError = useCallback(
    (field: string): string | undefined => {
      return errors[field];
    },
    [errors]
  );

  const hasErrors = Object.keys(errors).length > 0;

  return {
    validate,
    validateField,
    errors,
    setErrors,
    clearErrors,
    clearFieldError,
    hasErrors,
    getFieldError,
  };
}

/**
 * Fonction utilitaire pour valider des données sans hook
 */
export function validateData<T>(
  schema: ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; errors: ValidationError[] } {
  try {
    const validData = schema.parse(data);
    return { success: true, data: validData };
  } catch (error) {
    if (error instanceof ZodError) {
      const validationErrors: ValidationError[] = error.errors.map((err) => ({
        field: err.path.join('.'),
        message: err.message,
      }));
      return { success: false, errors: validationErrors };
    }
    throw error;
  }
}

/**
 * Fonction utilitaire pour formater les erreurs Zod en message unique
 */
export function formatZodErrors(error: ZodError): string {
  return error.errors.map((err) => err.message).join('. ');
}
